/**
 * Normalized accessorial catalog and per-carrier code maps.
 *
 * Every accessorial a shipper can request is one of the closed set of codes below. Carrier
 * adapters never see free-form strings: they translate normalized `(code, stop)` pairs through
 * `mapAccessorialsForCarrier`, which reports anything the carrier can't rate so the orchestrator
 * can skip that carrier or flag the option instead of silently under-quoting.
 *
 * Carrier codes marked `verified: true` come from the carrier research notes (carrier API guides).
 * Entries marked `verified: false` are the industry-common codes for that carrier and must be
 * confirmed against the carrier's published docs before live mode (task 99, QUESTIONS.md).
 */
import { isCrossBorder } from "./crossborder.js";
import type { Accessorial, Address, CarrierCode, HandlingUnit, ValidationIssue } from "./domain.js";

export type AccessorialStop = Accessorial["stop"];

export type AccessorialCategory =
  "equipment" | "location" | "handling" | "scheduling" | "regulatory";

/** Extra data an accessorial needs before it can be booked. */
export type AccessorialRequirement =
  "hazmat_details" | "appointment_window" | "notify_contact" | "max_dimension";

export interface AccessorialDefinition {
  code: NormalizedAccessorialCode;
  label: string;
  description: string;
  category: AccessorialCategory;
  /** Stops the service can apply to. */
  stops: readonly AccessorialStop[];
  requires: readonly AccessorialRequirement[];
}

export const ACCESSORIAL_CODES = [
  "liftgate",
  "residential",
  "limited_access",
  "inside",
  "appointment",
  "notify",
  "hazmat",
  "protect_from_freeze",
  "overlength",
  "sort_segregate",
  "construction",
  "trade_show",
  "airport",
  "military",
  "school",
  "place_of_worship",
  "farm",
  "nursing_home",
  "hospital",
  "pallet_jack",
  "customs_border",
] as const;
export type NormalizedAccessorialCode = (typeof ACCESSORIAL_CODES)[number];

const PD = ["pickup", "delivery"] as const;
const SHIP = ["shipment"] as const;

function def(
  code: NormalizedAccessorialCode,
  label: string,
  description: string,
  category: AccessorialCategory,
  stops: readonly AccessorialStop[],
  requires: readonly AccessorialRequirement[] = [],
): AccessorialDefinition {
  return { code, label, description, category, stops, requires };
}

export const ACCESSORIAL_CATALOG: Readonly<
  Record<NormalizedAccessorialCode, AccessorialDefinition>
> = {
  liftgate: def(
    "liftgate",
    "Liftgate",
    "Truck with a liftgate; no dock or forklift at the stop.",
    "equipment",
    PD,
  ),
  residential: def(
    "residential",
    "Residential",
    "Home, home business or apartment address.",
    "location",
    PD,
  ),
  limited_access: def(
    "limited_access",
    "Limited access",
    "Restricted site such as a storage unit, mini-mall, strip center or gated facility.",
    "location",
    PD,
  ),
  inside: def(
    "inside",
    "Inside",
    "Freight moved beyond the dock door or threshold.",
    "handling",
    PD,
  ),
  appointment: def(
    "appointment",
    "Appointment",
    "Pickup or delivery only within a scheduled appointment.",
    "scheduling",
    PD,
    ["appointment_window"],
  ),
  notify: def(
    "notify",
    "Notify before delivery",
    "Carrier calls the consignee before delivering.",
    "scheduling",
    ["delivery"],
    ["notify_contact"],
  ),
  hazmat: def(
    "hazmat",
    "Hazardous materials",
    "Regulated hazardous materials (49 CFR).",
    "regulatory",
    SHIP,
    ["hazmat_details"],
  ),
  protect_from_freeze: def(
    "protect_from_freeze",
    "Protect from freezing",
    "Freight must be kept above freezing in transit.",
    "handling",
    SHIP,
  ),
  overlength: def(
    "overlength",
    "Overlength",
    "A handling unit is 8 ft (96 in) or longer.",
    "handling",
    SHIP,
    ["max_dimension"],
  ),
  sort_segregate: def(
    "sort_segregate",
    "Sort and segregate",
    "Carrier sorts mixed freight by mark, size or type at delivery.",
    "handling",
    SHIP,
  ),
  construction: def(
    "construction",
    "Construction site",
    "Active construction or job site.",
    "location",
    PD,
  ),
  trade_show: def(
    "trade_show",
    "Trade show",
    "Convention center or trade show venue.",
    "location",
    PD,
  ),
  airport: def("airport", "Airport", "Airport or air freight facility.", "location", PD),
  military: def(
    "military",
    "Military base",
    "Military installation; ID and gate checks.",
    "location",
    PD,
  ),
  school: def("school", "School", "School, college or university.", "location", PD),
  place_of_worship: def(
    "place_of_worship",
    "Place of worship",
    "Church, temple, mosque or similar.",
    "location",
    PD,
  ),
  farm: def(
    "farm",
    "Farm or ranch",
    "Farm, ranch or other rural agricultural site.",
    "location",
    PD,
  ),
  nursing_home: def("nursing_home", "Nursing home", "Nursing home, assisted living or senior living community.", "location", PD),
  hospital: def("hospital", "Hospital", "Hospital or medical center.", "location", PD),
  pallet_jack: def(
    "pallet_jack",
    "Pallet jack",
    "Driver brings a pallet jack to move freight.",
    "equipment",
    PD,
  ),
  customs_border: def(
    "customs_border",
    "Border crossing",
    "US-Canada border crossing: customs broker handoff and border processing (PARS/PAPS).",
    "regulatory",
    SHIP,
  ),
};

export function isAccessorialCode(code: unknown): code is NormalizedAccessorialCode {
  return (
    typeof code === "string" && Object.prototype.hasOwnProperty.call(ACCESSORIAL_CATALOG, code)
  );
}

export function getAccessorial(code: string): AccessorialDefinition | undefined {
  return isAccessorialCode(code) ? ACCESSORIAL_CATALOG[code] : undefined;
}

// ---------------------------------------------------------------------------------------------
// Carrier code maps
// ---------------------------------------------------------------------------------------------

export type MappedCarrier = Exclude<CarrierCode, "mock">;

export interface CarrierAccessorialCode {
  /** The carrier's code, or a request-field path for carriers that use booleans (TForce shipment flags). */
  code: string;
  /** True when confirmed in the carrier's API guide; false = industry-common, confirm before live. */
  verified: boolean;
}

type StopMap = Partial<Record<AccessorialStop, CarrierAccessorialCode>>;
export type CarrierAccessorialMap = Partial<Record<NormalizedAccessorialCode, StopMap>>;

const u = (code: string): CarrierAccessorialCode => ({ code, verified: false });
const v = (code: string): CarrierAccessorialCode => ({ code, verified: true });
const pd = (pickup: CarrierAccessorialCode, delivery: CarrierAccessorialCode): StopMap => ({
  pickup,
  delivery,
});
const del = (delivery: CarrierAccessorialCode): StopMap => ({ delivery });
const ship = (shipment: CarrierAccessorialCode): StopMap => ({ shipment });

export const CARRIER_ACCESSORIAL_CODES: Readonly<Record<MappedCarrier, CarrierAccessorialMap>> = {
  // XPO Rating guide 6.4 (listRateQuoteAccessorials) and BOL guide 3.2 (`additionalService.accsrlCode`), read
  // 2026-10-21 (docs/carriers/XPO.md). The rating API rates a delivery appointment as TDC; the BOL spells it APT
  // (the XPO adapter translates when booking). Pickup appointments, airport/military/school/farm and pallet jack
  // are not listed by either guide.
  xpo: {
    liftgate: pd(v("OLG"), v("DLG")),
    residential: pd(v("RSO"), v("RSD")),
    limited_access: pd(v("OLA"), v("DLA")),
    inside: pd(v("OIP"), v("DID")),
    appointment: del(v("TDC")),
    notify: del(v("DNC")),
    hazmat: ship(v("ZHM")),
    protect_from_freeze: ship(v("XFZ")),
    overlength: ship(v("ELS")),
    sort_segregate: ship(v("SRT")),
    construction: pd(v("OCS"), v("CSD")),
    trade_show: pd(v("PEO"), v("PED")),
    // DRA is XPO's border-crossing service, documented for US-Mexico; its use on US-Canada lanes is unconfirmed.
    customs_border: ship(u("DRA")),
  },
  // Rate Estimate API v1.0 "Values: accessorialCodes" (docs/integrations/odfl/rate.txt). Services the rate guide
  // does not list (trade show, military, farm, pallet jack) are not mapped, so they are reported unsupported
  // instead of sent with an invented code. Church shares CDC/CPC with school, and a code may map once.
  // Hazmat, overlength and cross-border are rated from documented item fields (hazardous / hazardousId, the
  // dimensions) and ODFL's own border charges, so the adapter never sends their codes: HAZ and OVR are the eBOL
  // codes, CSC the rate response's "Border Documentation Fee".
  odfl: {
    liftgate: pd(v("HYO"), v("HYD")),
    residential: pd(v("RPC"), v("RDC")),
    limited_access: pd(v("LPC"), v("LDC")),
    inside: pd(v("IPC"), v("IDC")),
    appointment: pd(v("CP"), v("CA")),
    notify: del(v("ARN")),
    hazmat: ship(v("HAZ")),
    overlength: ship(v("OVR")),
    customs_border: ship(u("CSC")),
    protect_from_freeze: ship(v("PFF")),
    sort_segregate: ship(v("SSC")),
    construction: pd(v("CSP"), v("CSD")),
    school: pd(v("CPC"), v("CDC")),
    airport: pd(v("AIP"), v("AID")),
  },
  estes: {
    liftgate: pd(u("LGATEP"), u("LGATE")),
    residential: pd(u("RES"), u("HD")),
    limited_access: pd(u("LAP"), u("LAD")),
    inside: pd(u("INSP"), v("INP")),
    appointment: pd(u("APTP"), v("APT")),
    notify: del(u("NCM")),
    hazmat: ship(u("HAZ")),
    protect_from_freeze: ship(u("FRZ")),
    overlength: ship(u("OVL")),
    construction: pd(u("CNSP"), u("CNST")),
    trade_show: pd(u("TSP"), u("TSD")),
    military: pd(u("MILP"), u("MIL")),
    school: pd(u("SCHP"), u("SCH")),
    farm: pd(u("FRMP"), u("FRM")),
    airport: pd(u("ARPP"), u("ARP")),
    place_of_worship: pd(u("CHRP"), u("CHR")),
    customs_border: ship(u("CBX")),
  },
  saia: {
    liftgate: pd(u("LiftgateService"), u("LiftgateServiceDelivery")),
    residential: pd(u("ResidentialPickup"), u("ResidentialDelivery")),
    limited_access: pd(u("LimitedAccessPickup"), u("LimitedAccessDelivery")),
    inside: pd(u("InsidePickup"), u("InsideDelivery")),
    appointment: pd(u("AppointmentPickup"), u("AppointmentDelivery")),
    notify: del(u("ArrivalNotice")),
    hazmat: ship(u("Hazardous")),
    protect_from_freeze: ship(u("FreezableProtection")),
    overlength: ship(u("ExcessiveLength")),
    sort_segregate: ship(u("SortAndSegregate")),
    construction: pd(u("ConstructionPickup"), u("ConstructionDelivery")),
    trade_show: pd(u("TradeshowPickup"), u("TradeshowDelivery")),
    customs_border: ship(u("CustomsBorderCrossing")),
  },
  rl: {
    liftgate: pd(u("OriginLiftgate"), u("DestinationLiftgate")),
    residential: pd(u("ResidentialPickup"), u("ResidentialDelivery")),
    limited_access: pd(u("LimitedAccessPickup"), u("LimitedAccessDelivery")),
    inside: pd(u("InsidePickup"), u("InsideDelivery")),
    appointment: del(u("DeliveryAppointment")),
    notify: del(u("DeliveryNotification")),
    hazmat: ship(u("Hazmat")),
    protect_from_freeze: ship(u("Freezable")),
    sort_segregate: ship(u("SortAndSegregate")),
    trade_show: pd(u("TradeShowPickup"), u("TradeShowDelivery")),
    airport: pd(u("AirportPickup"), u("AirportDelivery")),
    customs_border: ship(u("BorderCrossing")),
  },
  abf: {
    liftgate: pd(u("LGP"), u("LGD")),
    residential: pd(u("RSP"), u("RSD")),
    limited_access: pd(u("LAP"), u("LAD")),
    inside: pd(u("IPU"), u("IDL")),
    appointment: pd(u("APP"), u("APD")),
    notify: del(u("NOT")),
    hazmat: ship(u("HAZ")),
    protect_from_freeze: ship(u("FRZ")),
    overlength: ship(u("EXL")),
    sort_segregate: ship(u("SRT")),
    construction: pd(u("CSP"), u("CSD")),
    trade_show: pd(u("TSP"), u("TSD")),
    customs_border: ship(u("CBC")),
  },
  fedex_freight: {
    liftgate: pd(u("LIFTGATE_PICKUP"), u("LIFTGATE_DELIVERY")),
    residential: pd(u("RESIDENTIAL_PICKUP"), u("RESIDENTIAL_DELIVERY")),
    limited_access: pd(u("LIMITED_ACCESS_PICKUP"), u("LIMITED_ACCESS_DELIVERY")),
    inside: pd(u("INSIDE_PICKUP"), u("INSIDE_DELIVERY")),
    appointment: del(u("CUSTOM_DELIVERY_WINDOW")),
    notify: del(u("CALL_BEFORE_DELIVERY")),
    hazmat: ship(u("DANGEROUS_GOODS")),
    protect_from_freeze: ship(u("PROTECTION_FROM_FREEZING")),
    overlength: ship(u("EXTREME_LENGTH")),
    customs_border: ship(u("CROSS_BORDER")),
  },
  tforce: {
    liftgate: pd(u("serviceOptions.pickup:LIFO"), u("serviceOptions.delivery:LIFD")),
    residential: pd(u("shipFrom.isResidential"), u("shipTo.isResidential")),
    limited_access: pd(u("serviceOptions.pickup:LAPU"), u("serviceOptions.delivery:LADL")),
    inside: pd(u("serviceOptions.pickup:INPU"), u("serviceOptions.delivery:INDE")),
    appointment: del(u("serviceOptions.delivery:APPT")),
    notify: del(u("serviceOptions.delivery:NTFY")),
    hazmat: ship(u("serviceOptions.shipment:hazmat")),
    protect_from_freeze: ship(v("serviceOptions.shipment.freezableProtection")),
    sort_segregate: ship(v("serviceOptions.shipment.sortAndSegregate")),
    customs_border: ship(u("serviceOptions.shipment:borderCrossing")),
  },
  // EagleLoad API v2 (CreateQuoteRequest): services are its origin/destination accessorial names; location types
  // are its addressType values. The adapter's own mapping (carriers/eagleload/mapping.ts) builds the request.
  eagleload: {
    liftgate: pd(v("origin.accessorials:Liftgate Pickup"), v("destination.accessorials:Liftgate Delivery")),
    inside: pd(v("origin.accessorials:Inside Pickup"), v("destination.accessorials:Inside Delivery")),
    appointment: del(v("destination.accessorials:Delivery Appointment")),
    notify: del(v("destination.accessorials:Notify Before Delivery")),
    hazmat: ship(v("origin.accessorials:Hazardous Material")),
    protect_from_freeze: ship(v("origin.accessorials:Protect From Freeze")),
    overlength: ship(v("origin.accessorials:Over Dimensions")),
    sort_segregate: ship(v("destination.accessorials:Sort/Segregate Delivery")),
    residential: pd(v("origin.addressType:Residential"), v("destination.addressType:Residential")),
    limited_access: pd(v("origin.addressType:Limited Access"), v("destination.addressType:Limited Access")),
    trade_show: pd(v("origin.addressType:Trade Show"), v("destination.addressType:Trade Show")),
    construction: pd(v("origin.addressType:Construction Site"), v("destination.addressType:Construction Site")),
    airport: pd(v("origin.addressType:Airport"), v("destination.addressType:Airport")),
    military: pd(v("origin.addressType:Military Installation"), v("destination.addressType:Military Installation")),
    school: pd(v("origin.addressType:School"), v("destination.addressType:School")),
    place_of_worship: pd(v("origin.addressType:Church"), v("destination.addressType:Church")),
    farm: pd(v("origin.addressType:Farm"), v("destination.addressType:Farm")),
    nursing_home: pd(v("origin.addressType:Nursing Home"), v("destination.addressType:Nursing Home")),
    hospital: pd(v("origin.addressType:Hospital"), v("destination.addressType:Hospital")),
  },
  // WWEX Speedship V4 shopFlow: services are boolean flags on the shipment, location types on the stops
  // (Postman collection, docs/carriers/WWEX.md). The adapter's own mapping (carriers/wwex/mapping.ts) builds the request.
  wwex: {
    liftgate: pd(v("shipment.liftgatePickupFlag"), v("shipment.liftgateDeliveryFlag")),
    residential: pd(v("shipment.residentialPickupFlag"), v("destinationAddress.locationType:RESIDENTIAL")),
    limited_access: pd(v("originAddress.locationType:LIMITED_ACCESS"), v("destinationAddress.locationType:LIMITED_ACCESS")),
    inside: pd(v("shipment.insidePickupFlag"), v("shipment.insideDeliveryFlag")),
    appointment: del(v("shipment.appointmentDeliveryFlag")),
    notify: del(v("shipment.notifyBeforeDeliveryFlag")),
    hazmat: ship(v("shippedItem.isHazMat")),
    protect_from_freeze: ship(v("shipment.protectionFromColdFlag")),
    sort_segregate: ship(v("shipment.sortAndSegregateFlag")),
    construction: pd(v("shipment.constructionSitePickupFlag"), v("shipment.constructionSiteDeliveryFlag")),
    trade_show: pd(v("shipment.tradeshowPickupFlag"), v("shipment.tradeshowDeliveryFlag")),
  },
  // AmFast has no API: codes are the plain-language lines written into the tender email.
  amfast: {
    liftgate: pd(u("LIFTGATE PICKUP"), u("LIFTGATE DELIVERY")),
    residential: pd(u("RESIDENTIAL PICKUP"), u("RESIDENTIAL DELIVERY")),
    limited_access: pd(u("LIMITED ACCESS PICKUP"), u("LIMITED ACCESS DELIVERY")),
    inside: pd(u("INSIDE PICKUP"), u("INSIDE DELIVERY")),
    appointment: pd(u("APPOINTMENT PICKUP"), u("APPOINTMENT DELIVERY")),
    notify: del(u("NOTIFY BEFORE DELIVERY")),
    hazmat: ship(u("HAZMAT")),
    protect_from_freeze: ship(u("PROTECT FROM FREEZE")),
    overlength: ship(u("OVERLENGTH")),
    sort_segregate: ship(u("SORT AND SEGREGATE")),
    construction: pd(u("CONSTRUCTION SITE PICKUP"), u("CONSTRUCTION SITE DELIVERY")),
    trade_show: pd(u("TRADE SHOW PICKUP"), u("TRADE SHOW DELIVERY")),
  },
};

// ---------------------------------------------------------------------------------------------
// Validation, mapping, derivation
// ---------------------------------------------------------------------------------------------

const isRecord = (x: unknown): x is Record<string, unknown> => typeof x === "object" && x !== null;

/** Own-property lookup so prototype keys ("constructor", "__proto__") never resolve. */
function ownGet<T>(obj: Partial<Record<string, T>> | undefined, key: unknown): T | undefined {
  return obj && typeof key === "string" && Object.prototype.hasOwnProperty.call(obj, key)
    ? obj[key]
    : undefined;
}

function isAccessorialStop(def: AccessorialDefinition, stop: unknown): stop is AccessorialStop {
  return typeof stop === "string" && (def.stops as readonly string[]).includes(stop);
}

/** Validates against the closed catalog: unknown code, stop not allowed, duplicate (code, stop). */
export function validateAccessorials(
  list: readonly Accessorial[],
  path = "accessorials",
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const seen = new Set<string>();
  list.forEach((a: unknown, i) => {
    if (!isRecord(a)) {
      issues.push({ path: `${path}[${i}]`, message: "must be an accessorial object" });
      return;
    }
    const def = getAccessorial(a.code as string);
    if (!def) {
      issues.push({ path: `${path}[${i}].code`, message: "unknown accessorial" });
      return;
    }
    if (!isAccessorialStop(def, a.stop))
      issues.push({
        path: `${path}[${i}].stop`,
        message: `must be one of ${def.stops.join(", ")}`,
      });
    const key = `${String(a.stop)}:${def.code}`;
    if (seen.has(key)) issues.push({ path: `${path}[${i}]`, message: "duplicate" });
    seen.add(key);
  });
  return issues;
}

export interface MappedAccessorial {
  accessorial: Accessorial;
  carrierCode: CarrierAccessorialCode;
}

export interface CarrierAccessorialMapping {
  carrier: MappedCarrier;
  mapped: MappedAccessorial[];
  /** Requested services this carrier has no code for: skip the carrier or flag the option. */
  unsupported: Accessorial[];
  /** True when any mapped code is not yet confirmed against carrier docs. */
  hasUnverified: boolean;
}

export function mapAccessorialsForCarrier(
  carrier: MappedCarrier,
  list: readonly Accessorial[],
): CarrierAccessorialMapping {
  const table = ownGet(CARRIER_ACCESSORIAL_CODES, carrier);
  const mapped: MappedAccessorial[] = [];
  const unsupported: Accessorial[] = [];
  for (const a of list) {
    const entry =
      isRecord(a) && isAccessorialCode(a.code)
        ? ownGet<CarrierAccessorialCode>(ownGet(table, a.code), a.stop)
        : undefined;
    if (entry) mapped.push({ accessorial: a, carrierCode: entry });
    else unsupported.push(a);
  }
  return {
    carrier,
    mapped,
    unsupported,
    hasUnverified: mapped.some((m) => !m.carrierCode.verified),
  };
}

/** Reverse lookup for parsing carrier charge lines back into normalized accessorials. */
export function fromCarrierCode(
  carrier: MappedCarrier,
  carrierCode: string,
): Accessorial | undefined {
  const table = ownGet(CARRIER_ACCESSORIAL_CODES, carrier);
  if (!table || typeof carrierCode !== "string") return undefined;
  for (const code of ACCESSORIAL_CODES) {
    const stops = ownGet(table, code);
    if (!stops) continue;
    for (const stop of Object.keys(stops) as AccessorialStop[]) {
      if (stops[stop]?.code === carrierCode) return { code, stop };
    }
  }
  return undefined;
}

/** Longest dimension (inches) at or above which a unit is overlength. */
export const OVERLENGTH_THRESHOLD_IN = 96;

/**
 * Accessorials implied by the freight itself (hazmat units, overlength units) and, when `lane` is
 * given, by the lane (a border crossing on a US<->CA/MX lane). Returned only when
 * not already requested, so callers can merge them and show the shipper why they were added.
 */
export function deriveAccessorials(
  units: readonly Pick<HandlingUnit, "dims" | "hazmat">[],
  requested: readonly Accessorial[] = [],
  lane?: { origin: Pick<Address, "country">; destination: Pick<Address, "country"> },
): Accessorial[] {
  const has = (code: NormalizedAccessorialCode): boolean =>
    requested.some((a: unknown) => isRecord(a) && a.code === code && a.stop === "shipment");
  const out: Accessorial[] = [];
  // Per-unit checks in a loop: a NaN dimension on one unit must not hide another unit's overlength
  // (Math.max propagates NaN), and spreading a huge unit list into Math.max overflows the stack.
  let hazmat = false;
  let overlength = false;
  for (const x of units as readonly unknown[]) {
    if (!isRecord(x)) continue;
    // null is how a JSON column says "no hazmat", not a declaration.
    if (x.hazmat !== undefined && x.hazmat !== null) hazmat = true;
    const d = x.dims;
    if (
      isRecord(d) &&
      [d.lengthIn, d.widthIn, d.heightIn].some(
        (n) => typeof n === "number" && n >= OVERLENGTH_THRESHOLD_IN,
      )
    )
      overlength = true;
  }
  if (!has("hazmat") && hazmat) out.push({ code: "hazmat", stop: "shipment" });
  if (!has("overlength") && overlength) out.push({ code: "overlength", stop: "shipment" });
  if (!has("customs_border") && lane && isCrossBorder(lane))
    out.push({ code: "customs_border", stop: "shipment" });
  return out;
}

/** An accessorial added for the shipper, with the plain-language reason shown next to it. */
export interface AutoAccessorial extends Accessorial {
  reason: string;
}

/**
 * Every accessorial the freight or lane implies (hazmat, overlength, border crossing), with a
 * shipper-facing reason. Unlike `deriveAccessorials` this ignores what was requested, so it can
 * explain a stored quote whose accessorials already include them.
 */
export function impliedAccessorials(
  units: readonly Pick<HandlingUnit, "dims" | "hazmat">[],
  lane?: { origin: Pick<Address, "country">; destination: Pick<Address, "country"> },
): AutoAccessorial[] {
  return deriveAccessorials(units, [], lane).map((a) => ({
    ...a,
    reason:
      a.code === "hazmat"
        ? "A freight line is declared hazardous"
        : a.code === "overlength"
          ? `A handling unit is ${OVERLENGTH_THRESHOLD_IN} in or longer`
          : `Lane crosses the border (${lane?.origin.country} to ${lane?.destination.country})`,
  }));
}

/**
 * `req.accessorials` plus the border-crossing accessorial on a US<->CA/MX lane, without duplicates.
 * Hazmat and overlength are left to the adapters, which already derive them from the units.
 */
export function withLaneAccessorials<
  R extends {
    accessorials?: readonly Accessorial[];
    origin: { address: Pick<Address, "country"> };
    destination: { address: Pick<Address, "country"> };
  },
>(req: R): R {
  const requested = req.accessorials ?? [];
  const extra = deriveAccessorials([], requested, {
    origin: req.origin.address,
    destination: req.destination.address,
  });
  return extra.length === 0 ? req : { ...req, accessorials: [...requested, ...extra] };
}
